/**
 * Building blocks shared by every catalog schema.
 *
 * The catalog is a *curated data product* (PLAN §6), so the schemas here are
 * deliberately strict: unknown keys are rejected, every numeric field is
 * bounded by what is physically possible, and every entry has to declare where
 * its numbers came from ({@link provenanceSchema}).
 */

import { z } from 'zod';

// ---------------------------------------------------------------------------
// Identity
// ---------------------------------------------------------------------------

/** Lower-case kebab-case, e.g. `weyermann-pilsner`. No leading/trailing dash. */
export const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * A stable catalog identifier.
 *
 * IDs are part of the public contract: recipes store them, the AI assistant's
 * `search_catalog` tool returns them (PLAN §7.1) and they must never be
 * recycled for a different product. Where a product name alone is ambiguous
 * across makers, the maker is part of the slug (`weyermann-pilsner` vs.
 * `bestmalz-pilsner`).
 */
export const slugSchema = z
  .string()
  .min(2)
  .max(80)
  .regex(SLUG_PATTERN, 'must be lower-case kebab-case');

// ---------------------------------------------------------------------------
// Provenance
// ---------------------------------------------------------------------------

/**
 * Where an entry's numbers came from.
 *
 * - `spec-sheet` — the values are the maker's published specification (or the
 *   mid-point of a published range) for this exact product.
 * - `representative` — typical values for the product class, not published by
 *   the maker for this SKU. Good enough to design a recipe with, never quoted
 *   as fact.
 *
 * We never invent precision. If a figure is not published, the entry carries
 * class-typical values and is marked `representative`.
 */
export const provenanceSchema = z.enum(['spec-sheet', 'representative']);

/** @see provenanceSchema */
export type Provenance = z.infer<typeof provenanceSchema>;

/**
 * The maker's own page for this exact product.
 *
 * Optional, and absent far more often than present. Three rules govern it, and
 * they are the same rule the numbers follow — **a link is a fact, not a
 * guess**:
 *
 * 1. **Fetched, never inferred.** Every URL in this package was retrieved and
 *    read before it was written down. A plausible-looking address assembled
 *    from a product name and a URL pattern is a fabrication that happens to be
 *    shaped like a link, and it fails in the worst possible way: silently, in
 *    front of a brewer who trusted it.
 * 2. **The maker's page, not a shop's.** Retailers reshuffle catalogues and go
 *    out of business; a maltster's own product page is the closest thing to a
 *    durable address, and it is where the spec sheet lives.
 * 3. **This product, not its family.** A link to a category listing is not a
 *    product page. Where a maker publishes no page for an individual SKU, the
 *    field is simply absent — which is the honest answer and reads as one.
 *
 * ## What counts as a page
 *
 * A **maker-hosted spec-sheet PDF** counts where the maker publishes no HTML
 * page per product. Briess is the case that forced the question: every malt is
 * described inline on a shared category page and the only per-product document
 * is the PDF. Refusing it bought nothing — the PDF is the more authoritative
 * document of the two, and it is exactly what the brewer wanted.
 *
 * A query string is kept only where it *is* the address — White Labs addresses
 * a strain by an opaque record id (`?id=218&type=YEAST`), and its own listing
 * links that way. Tracking and referral parameters are stripped; see
 * `product-urls.test.ts`, which refuses them.
 *
 * ## Why `http` is possible but fenced
 *
 * A handful of small maltsters still serve no TLS at all — Sugar Creek's store
 * fails the handshake outright. Their pages are real, so the choice was a
 * plain link or nothing, and nothing helps no one. `http` is therefore allowed
 * by this schema but held to an explicit host allowlist in
 * `product-urls.test.ts`: an insecure link has to be a deliberate, named
 * exception rather than something that quietly slips in the next time a link
 * is added.
 *
 * Absent on every `Generic` class entry by definition: there is no maker, so
 * there is no page.
 */
export const productUrlSchema = z.url({ protocol: /^https?$/ }).max(300);

// ---------------------------------------------------------------------------
// Prose
// ---------------------------------------------------------------------------

/**
 * A short description written in our own words.
 *
 * Specifications are facts and therefore free to restate; prose is not. Every
 * description in this package is original (PLAN §6, licensing note).
 */
export const descriptionSchema = z.string().min(20).max(600);

/** Free-form lower-case search/filter tags (`noble`, `citrus`, `stone-fruit`). */
export const tagsSchema = z.array(z.string().regex(SLUG_PATTERN)).max(24);

// ---------------------------------------------------------------------------
// Ranges
// ---------------------------------------------------------------------------

/**
 * An inclusive `min`–`max` band.
 *
 * Published specs are nearly always ranges (alpha acid varies by crop year,
 * attenuation by pitch rate, style vitals by definition). Storing a range and
 * letting the caller pick the mid-point is honest; storing a single number is
 * not.
 *
 * @param min - Lowest value the field may take.
 * @param max - Highest value the field may take.
 */
export function range(min: number, max: number) {
  return z
    .strictObject({
      /** Inclusive lower bound. */
      min: z.number().min(min).max(max),
      /** Inclusive upper bound. */
      max: z.number().min(min).max(max),
    })
    .refine((r) => r.min <= r.max, { message: 'min must be <= max' });
}

/** An inclusive numeric band. @see range */
export interface Range {
  readonly min: number;
  readonly max: number;
}

/** The arithmetic mid-point of a range — the value calculators default to. */
export function midpoint(r: Range): number {
  return (r.min + r.max) / 2;
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

/** A percentage 0–100. */
export const percentSchema = z.number().min(0).max(100);

/** ISO-3166 style country names as we display them, not codes. */
export const originSchema = z.string().min(2).max(40);
